/**
 * Created by nanhuijuan on 2019/12/17
 */
import * as Sentry from '@sentry/nextjs';
import { getToken } from './tokenManager';
import { clearToken } from './tokenManager';

function parseJSON(response) {
  return response.json();
}

/**
 * 处理响应，兼容 401 无响应体的情况
 * @param {Response} res - fetch 响应对象
 * @param {string} nextPath - 401 时跳转的路径
 * @returns {Promise<any>} 解析后的 JSON 数据
 */
async function handleAuthResponse(res, nextPath = '/') {
  // 先检查 HTTP 状态码，兼容无响应体的 401
  if (res.status === 401) {
    if (typeof window !== 'undefined') {
      window.location.href = nextPath;
      clearToken();
    }
    return Promise.reject(new Error('Unauthorized'));
  }
  // 尝试解析 JSON，兼容空响应体
  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}

/**
 *
 * @param data {meta: {code: 200, message: "OK"}, data: {...}}
 * @returns {*}
 */
function checkMeta(data) {
  if (!data || !data.meta) {
    throw new Error('no meta data');
  } else {
    const { meta, ...d } = data;
    if (!String(meta.code).startsWith('2')) {
      throw new Error(meta);
    } else {
      return d;
    }
  }
}

/**
 * Requests a URL, returning a promise.
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 * @return {object}           An object containing either "data" or "err"
 */
export default function request(url, options) {
  if (!options) {
    options = {};
  }

  return fetch(url, { ...options, credentials: 'include' })
    .then(checkStatus)
    .then(parseJSON)
    .then(checkMeta);
}

export function post(url, data) {
  return request(url, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function postApi(url, data, headers = {}) {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      ...headers,
    },
    body: JSON.stringify(data),
  }).then((res) => res.json());
}

export function deleteApi(url, data, headers = {}) {
  return fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      ...headers,
    },
    body: JSON.stringify(data),
  }).then((res) => res.json());
}

export function put(url, data) {
  const option = {
    method: 'PUT',
  };

  if (data) {
    option.body = JSON.stringify(data);
    option.headers = {
      'Content-Type': 'application/json',
    };
  }
  return request(url, option);
}

export function del(url, data) {
  const option = {
    method: 'DELETE',
  };

  if (data) {
    option.body = JSON.stringify(data);
    option.headers = {
      'Content-Type': 'application/json',
    };
  }
  return request(url, option);
}

export function get(url) {
  return request(url);
}

export function download(url, options) {
  let filename = '';
  if (!options) {
    options = {};
  }

  return fetch(url, { ...options, headers: { Accept: 'application/octet-stream' } })
    .then(checkStatus)
    .then((resp) => {
      filename = resp.headers.get('content-disposition');
      filename = filename.match(/filename="(.+?)"/)[1];
      return resp.blob();
    })
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
      a.click();
      a.remove();
    });
}

export async function getWithAuth(url, headers = {}, nextPath = '/') {
  const accessToken = getToken(); // 拿到用户的token
  if (!accessToken) {
    // 如果没有accessToken，跳转到登录页面
    if (typeof window !== 'undefined') {
      window.location.href = nextPath;
    }
    return Promise.reject(new Error('No access token'));
  }
  return fetch(url, {
    method: 'GET',
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json; charset=utf-8',
    },
  })
    .then((res) => handleAuthResponse(res, nextPath))
    .then((result) => {
      if (result?.meta?.code === 401000) {
        // 如果token过期，跳转到登录页面
        if (typeof window !== 'undefined') {
          window.location.href = nextPath;
          clearToken();
        }
      }
      return result;
    })
    .catch((err) => {
      Sentry.captureException(err);
    });
}

export function postWithAuth(url, data, headers = {}, nextPath = '/') {
  const accessToken = getToken(); // 拿到用户的token
  if (!accessToken) {
    // 如果没有accessToken，跳转到登录页面
    if (typeof window !== 'undefined') {
      window.location.href = nextPath;
    }
    return Promise.reject(new Error('No access token'));
  }

  return fetch(url, {
    method: 'POST',
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify(data),
  })
    .then((res) => handleAuthResponse(res, nextPath))
    .then((result) => {
      if (!String(result?.code || result?.meta?.code).startsWith('2')) {
        Sentry.captureException(
          new Error(`postWithAuth API Error: ${JSON.stringify(result)}; url: ${url}; data:${JSON.stringify(data)}`)
        );
      }
      if (result?.meta?.code === 401000) {
        // 如果token过期，跳转到登录页面
        if (typeof window !== 'undefined') {
          window.location.href = nextPath;
          clearToken();
        }
      }
      return result;
    })
    .catch((e) => {
      Sentry.captureException(e);
    });
}

export function patchWithAuth(url, data, headers = {}, nextPath = '/') {
  const accessToken = getToken(); // 拿到用户的token
  if (!accessToken) {
    // 如果没有accessToken，跳转到登录页面
    if (typeof window !== 'undefined') {
      window.location.href = nextPath;
    }
    return Promise.reject(new Error('No access token'));
  }
  return fetch(url, {
    method: 'PATCH',
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify(data),
  })
    .then((res) => handleAuthResponse(res, nextPath))
    .then((result) => {
      if (!String(result?.code || result?.meta?.code).startsWith('2')) {
        Sentry.captureException(
          new Error(`patchWithAuth API Error: ${JSON.stringify(result)}; url: ${url}; data:${JSON.stringify(data)}`)
        );
      }
      return result;
    })
    .catch((e) => {
      Sentry.captureException(e);
    });
}

export function putWithAuth(url, data, headers = {}, nextPath = '/') {
  const accessToken = getToken(); // 拿到用户的token
  return fetch(url, {
    method: 'PUT',
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify(data),
  })
    .then((res) => handleAuthResponse(res, nextPath))
    .then((result) => {
      if (!String(result?.code || result?.meta?.code).startsWith('2')) {
        Sentry.captureException(
          new Error(`putWithAuth API Error: ${JSON.stringify(result)}; url: ${url}; data:${JSON.stringify(data)}`)
        );
      }
      return result;
    })
    .catch((e) => {
      Sentry.captureException(e);
    });
}
